import React from 'react';
import moment from 'moment';
import { fetchConnector, FetchPNLAnalyticsPropsFromRedux } from '../dataFetcher';
import {
    P_ARA,
    P_ARA_C4,
    P_BLPG,
    P_BLPG3,
    P_BRT,
    P_CP,
    P_CP_C4,
    P_EPC,
    P_EPC_C4,
    P_FEI,
    P_LST,
    P_MOPJ,
    P_NWE_NAPHTHA,
    P_WTI,
    P_ARB,
    P_EW,
    P_FEI_CP,
    P_EPC_LST,
    productMap,
    ProductMeta,
} from '../../common/products';
import { SimpleTableColumnConfig, SimpleTableConfig, SimpleTableRow, TableSize } from '../../tables/types';
import { exposuresNumFormatter } from '../../common/exposures';
import { UniqueTableName } from '../../common/uniqueTableName';
import { AggFuncName } from '../../common/cellRangeAgg';
import { PNLAnalyticsEndpoint, PNLFetchStatus } from '../../actions/pnlAnalytics';
import { PNLGoodiesNonCompletedResponses } from '../PaperPlaceHolder';
import { BackOfficeMiscDataResp } from '../fetchTypes';
import { Tooltip } from '@material-ui/core';
import chroma from 'chroma-js';
import { makeStyles } from '@material-ui/core/styles';
import { SwissColors } from '../../common/stylesConsts';
import SimpleTable from '../../tables/SimpleTable';

type Props = FetchPNLAnalyticsPropsFromRedux & {};

const useStyles = makeStyles(() => ({
    bodyProductCell: {
        padding: '0 !important',
        border: '0 !important',
        height: '10px !important', // Needed so that the 100% height will work
    },
}));

const pivotColumns: Array<SimpleTableColumnConfig> = [
    {
        name: 'term_month_date',
        title: 'Term',
        bodyTemplate: (val: any) => {
            if (val === '') return val;
            return moment(val).format('MMM YY');
        },
    },
];

const productColumns = [
    P_FEI,
    P_LST,
    P_EPC,
    P_CP,
    P_CP_C4,
    P_ARA,
    P_ARA_C4,
    P_NWE_NAPHTHA,
    P_MOPJ,
    P_EPC_C4,
    P_BLPG,
    P_BLPG3,
    P_WTI,
    P_BRT,
    P_ARB,
    P_EW,
    P_FEI_CP,
    P_EPC_LST,
];
const productsColumnConfig: Array<SimpleTableColumnConfig> = productColumns.map((productName) => {
    let productMeta: ProductMeta = productMap.get(productName)!;
    return {
        name: `${productName}|price_dtd`,
        title: productMeta.title,
        textColor: (_) => 'black',
        headerBackgroundColor: productMeta.backgroundColor,
        headerTextColor: productMeta.textColor,
        customDisplay: (row: SimpleTableRow, rawVal: any) => {
            if (rawVal === undefined || rawVal === null) return <span>{''}</span>;
            let rawFloat = row[`${productName}|price_dtd`] as number;
            let displayVal = rawFloat.toFixed(2);
            let todayPrice = row[`${productName}|price`] ? (row[`${productName}|price`] as number).toFixed(2) : '';
            let previousDayPrice = '';
            if (row[`${productName}|price_m1`]) {
                previousDayPrice = (row[`${productName}|price_m1`] as number).toFixed(2);
            }
            let minNum: number | null = null;
            let minStr: string | null = null;
            if (row[`${productName}|_price_min`]) {
                minNum = row[`${productName}|_price_min`] as number;
                minStr = minNum.toFixed(2);
            }
            let maxNum: number | null = null;
            let maxStr: string | null = null;
            if (row[`${productName}|_price_max`]) {
                maxNum = row[`${productName}|_price_max`] as number;
                maxStr = maxNum.toFixed(2);
            }
            let customStyles: any = {
                width: '100%',
                height: '100%',
                display: 'block',
            };
            let minMax: number | null = rawFloat < 0 ? minNum : maxNum;
            if (minMax !== null) {
                let scale = rawFloat < 0 ? chroma.scale(['white', SwissColors.Red]) : chroma.scale(['white', SwissColors.Green]);
                let percent = (rawFloat as number) / minMax;
                customStyles['backgroundColor'] = scale(percent).hex();
            }

            return (
                <Tooltip
                    placement="top"
                    title={
                        <React.Fragment>
                            <p>
                                Today's Prices: <strong>{todayPrice}</strong>
                            </p>
                            <p>
                                Previous Day Price: <strong>{previousDayPrice}</strong>
                            </p>
                            <p>
                                6 month Min Price DtD: <strong>{minStr}</strong>
                            </p>
                            <p>
                                6 month Max Price DtD: <strong>{maxStr}</strong>
                            </p>
                        </React.Fragment>
                    }>
                    <span style={customStyles}>{displayVal}</span>
                </Tooltip>
            );
        },
    };
});

const allColumns = pivotColumns.concat(productsColumnConfig);

const config: SimpleTableConfig = {
    uniqueTableName: UniqueTableName.PricesDtD,
    title: 'DtD Prices',
    size: TableSize.ExtraSmall,
    columns: allColumns,
    grandTotal: false,
    aggFormatVal: (aggFuncName: AggFuncName, val: number) => {
        return aggFuncName === AggFuncName.Count ? val : exposuresNumFormatter(val);
    },
    pivotDepth: 0,
    stickyHeader: true,
};

const DtDPrices = (props: Props) => {
    const classes = useStyles();
    const backOfficeMiscData = props.pnlAnalytics.back_office_misc;

    if (backOfficeMiscData.status !== PNLFetchStatus.COMPLETED) {
        return (
            <PNLGoodiesNonCompletedResponses endpoint={PNLAnalyticsEndpoint.BackOfficeMisc} data={backOfficeMiscData} minHeight={'552px'} />
        );
    }
    config.columns.forEach((el, _) => {
        el.bodyCellsClassName = classes.bodyProductCell;
    });

    let respData = backOfficeMiscData.data! as BackOfficeMiscDataResp;

    return <SimpleTable data={respData.dtd_changes} config={config} />;
};

export default fetchConnector(DtDPrices);
